<script>
import Business from '@/services/Business';

const emptyPerson = () => ({
  first_name: '',
  name_prefix: '',
  last_name: '',
  email: '',
});

export default {
  props: {
    registration: {
      type: Object,
      default: () => ({}),
    },
    userLimit: {
      type: [Number, String],
      default: null,
    },
    remainingSeats: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      peopleCount: 1,
      people: [emptyPerson()],
      tryingToSubmit: false,
      errorMessage: '',
    };
  },
  computed: {
    hasRegistration() {
      return Boolean(this.registration?.customer_id && this.registration?.event_id);
    },
    registrantName() {
      const name = [this.registration?.name, this.registration?.surname]
        .filter(Boolean)
        .join(' ')
        .trim();
      return name || '—';
    },
    registrantEmail() {
      return this.registration?.email || '—';
    },
    registrantCompany() {
      return this.registration?.company_name || '—';
    },
    currentReservation() {
      const value = parseInt(this.registration?.reservation, 10);
      return isNaN(value) || value < 1 ? 1 : value;
    },
    parsedUserLimit() {
      const value = Number(this.userLimit);
      return isNaN(value) ? null : value;
    },
    parsedRemainingSeats() {
      const value = Number(this.remainingSeats);
      return isNaN(value) ? null : value;
    },
    maxPeopleCount() {
      const limits = [];
      if (this.parsedUserLimit !== null) {
        limits.push(this.parsedUserLimit);
      }
      if (this.parsedRemainingSeats !== null) {
        limits.push(this.parsedRemainingSeats + this.currentReservation);
      }
      if (!limits.length) {
        return null;
      }
      return Math.min(...limits);
    },
    canAddPerson() {
      return this.maxPeopleCount === null || this.peopleCount < this.maxPeopleCount;
    },
  },
  watch: {
    registration: {
      handler() {
        this.resetForm();
      },
      immediate: true,
      deep: true,
    },
    peopleCount(count) {
      this.syncPeopleArray(count);
    },
  },
  methods: {
    personLabel(index, person) {
      return this.isRegistrantPerson(person)
        ? 'Person 1 (registrant)'
        : `Person ${index + 1}`;
    },

    isRegistrantPerson(person) {
      if (!person) {
        return false;
      }

      const registrantEmail = String(this.registration?.email || '').trim().toLowerCase();
      const personEmail = String(person.email || '').trim().toLowerCase();
      const registrantName = this.registrantName.trim().toLowerCase();
      const personName = this.buildPersonName(person).toLowerCase();

      if (registrantEmail && personEmail && registrantEmail === personEmail) {
        return true;
      }

      return Boolean(
        registrantName &&
        registrantName !== '—' &&
        personName &&
        registrantName === personName
      );
    },

    canRemovePerson(person) {
      if (this.people.length <= 1) {
        return false;
      }
      return !this.isRegistrantPerson(person);
    },

    parseAttendeeName(fullName) {
      const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) {
        return { first_name: '', name_prefix: '', last_name: '' };
      }
      if (parts.length === 1) {
        return { first_name: parts[0], name_prefix: '', last_name: '' };
      }
      if (parts.length === 2) {
        return { first_name: parts[0], name_prefix: '', last_name: parts[1] };
      }
      return {
        first_name: parts[0],
        name_prefix: parts.slice(1, -1).join(' '),
        last_name: parts[parts.length - 1],
      };
    },

    buildPersonName(person) {
      return [person.first_name, person.name_prefix, person.last_name]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
    },

    buildPeopleFromRegistration() {
      const attendees = Array.isArray(this.registration?.attendees)
        ? this.registration.attendees
        : [];
      const reservation = this.currentReservation;

      if (attendees.length) {
        const people = attendees.map((attendee) => {
          const parsed = this.parseAttendeeName(attendee.reserved_for_name);
          return {
            first_name: parsed.first_name,
            name_prefix: parsed.name_prefix,
            last_name: parsed.last_name,
            email: attendee.reserved_for_email || '',
          };
        });

        // Keep registrant first so Remove never targets them by position.
        return people.sort((a, b) => {
          const aIsRegistrant = this.isRegistrantPerson(a) ? 0 : 1;
          const bIsRegistrant = this.isRegistrantPerson(b) ? 0 : 1;
          return aIsRegistrant - bIsRegistrant;
        });
      }

      const people = [];
      for (let i = 0; i < reservation; i += 1) {
        if (i === 0) {
          people.push({
            first_name: this.registration?.name || '',
            name_prefix: '',
            last_name: this.registration?.surname || '',
            email: this.registration?.email || '',
          });
        } else {
          people.push(emptyPerson());
        }
      }
      return people.length ? people : [emptyPerson()];
    },

    syncPeopleArray(count) {
      const size = Math.max(1, parseInt(count, 10) || 1);
      const current = this.people || [];
      const registrant = current.find((person) => this.isRegistrantPerson(person))
        || {
          first_name: this.registration?.name || '',
          name_prefix: '',
          last_name: this.registration?.surname || '',
          email: this.registration?.email || '',
        };
      const guests = current.filter((person) => !this.isRegistrantPerson(person));
      const next = [{ ...registrant }];

      for (let i = 0; i < size - 1; i += 1) {
        next.push(guests[i] ? { ...guests[i] } : emptyPerson());
      }

      this.people = next;
    },

    resetForm() {
      this.peopleCount = this.currentReservation;
      this.people = this.buildPeopleFromRegistration();
      this.errorMessage = '';
      this.tryingToSubmit = false;
    },

    decrementPeopleCount() {
      if (this.peopleCount > 1) {
        this.peopleCount -= 1;
      }
    },

    incrementPeopleCount() {
      if (this.canAddPerson) {
        this.peopleCount += 1;
      }
    },

    removePerson(index) {
      const person = this.people[index];
      if (!this.canRemovePerson(person)) {
        return;
      }
      this.people.splice(index, 1);
      this.peopleCount = this.people.length;
    },

    validatePeople() {
      for (let i = 0; i < this.people.length; i += 1) {
        const person = this.people[i];
        const label = this.personLabel(i, person);

        if (!String(person.first_name || '').trim()) {
          this.errorMessage = `${label}: first name is required.`;
          return false;
        }
        if (!String(person.last_name || '').trim()) {
          this.errorMessage = `${label}: last name is required.`;
          return false;
        }
      }
      return true;
    },

    buildAttendeesPayload() {
      return this.people.map((person) => ({
        email: String(person.email || '').trim(),
        name: this.buildPersonName(person),
      }));
    },

    async saveChanges() {
      if (!this.hasRegistration) {
        this.errorMessage = 'Registration data is incomplete.';
        return;
      }

      const reservation = parseInt(this.peopleCount, 10);
      if (isNaN(reservation) || reservation < 1) {
        this.errorMessage = 'Amount of people must be at least 1.';
        return;
      }
      if (this.people.length !== reservation) {
        this.errorMessage = 'Person details must match the selected amount of people.';
        return;
      }
      if (this.maxPeopleCount !== null && reservation > this.maxPeopleCount) {
        this.errorMessage = `Maximum allowed: ${this.maxPeopleCount} ticket(s).`;
        return;
      }
      if (!this.validatePeople()) {
        return;
      }

      this.tryingToSubmit = true;
      this.errorMessage = '';

      try {
        const response = await Business.updateUserReservations(this.registration.event_id, {
          reservation,
          customer_id: this.registration.customer_id,
          attendees: this.buildAttendeesPayload(),
        });

        if (response.data?.success) {
          this.$emit('onRefresh');
          this.$bvModal.hide('edit_user_reservations');
        } else {
          const err = response.data?.error;
          this.errorMessage = Array.isArray(err) ? err.join(', ') : err || 'Failed to save changes.';
        }
      } catch (error) {
        const err = error.response?.data?.error;
        this.errorMessage = Array.isArray(err) ? err.join(', ') : err || 'Failed to save changes.';
      } finally {
        this.tryingToSubmit = false;
      }
    },

    closeModal() {
      this.$bvModal.hide('edit_user_reservations');
    },
  },
};
</script>

<template>
  <b-modal
    id="edit_user_reservations"
    title="Edit registration"
    size="lg"
    centered
    @shown="resetForm"
    @hidden="resetForm"
  >
    <p class="text-muted small mb-3">
      Update ticket count and attendee details for this registration. Person 1 is the registrant ticket holder.
    </p>

    <div v-if="hasRegistration" class="registrant-summary mb-4">
      <div><strong>Business user:</strong> {{ registrantName }}</div>
      <div><strong>Account email:</strong> {{ registrantEmail }}</div>
      <div><strong>Company:</strong> {{ registrantCompany }}</div>
      <div><strong>Current tickets:</strong> {{ currentReservation }}</div>
    </div>

    <div class="d-flex flex-column align-items-center mb-4">
      <label class="fw-semibold mb-2">Amount of people</label>
      <div class="d-flex align-items-center attendee-stepper">
        <b-button
          variant="outline-secondary"
          class="stepper-btn"
          :disabled="peopleCount <= 1"
          @click="decrementPeopleCount"
        >
          −
        </b-button>
        <span class="stepper-value mx-3">{{ peopleCount }}</span>
        <b-button
          variant="outline-secondary"
          class="stepper-btn"
          :disabled="!canAddPerson"
          @click="incrementPeopleCount"
        >
          +
        </b-button>
      </div>
      <small v-if="parsedUserLimit !== null" class="text-muted mt-1">
        There is a limit of {{ parsedUserLimit }} ticket(s) per person.
      </small>
      <small v-if="parsedRemainingSeats !== null" class="text-muted">
        Up to {{ maxPeopleCount }} ticket(s) available for this registration.
      </small>
    </div>

    <div
      v-for="(person, index) in people"
      :key="'person-' + index"
      class="person-block mb-3"
    >
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h6 class="person-block-title mb-0">{{ personLabel(index, person) }}</h6>
        <a
          v-if="canRemovePerson(person)"
          href="javascript:void(0);"
          class="btn btn-sm btn-danger"
          title="Remove attendee"
          @click="removePerson(index)"
        >
          <i class="uil uil-trash-alt"></i>
        </a>
      </div>

      <b-form-group :label="'First name *'" :label-for="'edit_person_first_' + index">
        <b-form-input
          :id="'edit_person_first_' + index"
          v-model="person.first_name"
          placeholder="First name"
        />
      </b-form-group>

      <div class="row">
        <div class="col-md-4">
          <b-form-group :label="'Prefix'" :label-for="'edit_person_prefix_' + index">
            <b-form-input
              :id="'edit_person_prefix_' + index"
              v-model="person.name_prefix"
              placeholder="Prefix"
            />
          </b-form-group>
        </div>
        <div class="col-md-8">
          <b-form-group :label="'Last name *'" :label-for="'edit_person_last_' + index">
            <b-form-input
              :id="'edit_person_last_' + index"
              v-model="person.last_name"
              placeholder="Last name"
            />
          </b-form-group>
        </div>
      </div>

      <b-form-group :label="'Email'" :label-for="'edit_person_email_' + index" class="mb-0">
        <b-form-input
          :id="'edit_person_email_' + index"
          v-model="person.email"
          type="email"
          placeholder="Email address"
        />
      </b-form-group>
    </div>

    <p v-if="errorMessage" class="text-danger mt-3 mb-0">{{ errorMessage }}</p>

    <template #modal-footer>
      <b-button variant="light" @click="closeModal">Cancel</b-button>
      <b-button variant="primary" :disabled="tryingToSubmit" @click="saveChanges">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save changes
      </b-button>
    </template>
  </b-modal>
</template>

<style scoped>
.registrant-summary {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fafafa;
}

.person-block {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
  background: #fafafa;
}

.person-block-title {
  font-weight: 600;
}

.attendee-stepper .stepper-btn {
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  font-size: 1.25rem;
  line-height: 1;
  font-weight: 600;
}

.attendee-stepper .stepper-value {
  min-width: 2rem;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
}
</style>
